import {useContext, useEffect, useRef, useState} from 'react';
import {SearchContext} from '@features/search/context';
import {AsyncSearchModel, AsyncSearchState, SearchProgressStage} from '@features/search/interfaces';

export const COMPLETION_HIDE_DELAY_MS = 1000;

export function useSearchProgressBarVisibility(searchModel?: AsyncSearchModel): boolean {
    const searchContextFromProvider = useContext(SearchContext);
    const searchContext = searchModel ?? searchContextFromProvider;

    const [isBarVisible, setIsBarVisible] = useState(false);
    const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const completionHoldStartedRef = useRef(false);

    const isSearching = searchContext.state === AsyncSearchState.Searching;
    const isAllPolled = searchContext.progressStage === SearchProgressStage.AllPolled;

    const clearCompletionTimer = () => {
        if (completionTimerRef.current) {
            clearTimeout(completionTimerRef.current);
            completionTimerRef.current = null;
        }
    };

    useEffect(() => {
        if (isSearching) {
            clearCompletionTimer();
            completionHoldStartedRef.current = false;
            setIsBarVisible(true);
            return;
        }

        if (isAllPolled) {
            setIsBarVisible(true);

            if (!completionHoldStartedRef.current) {
                completionHoldStartedRef.current = true;
                clearCompletionTimer();

                completionTimerRef.current = setTimeout(() => {
                    setIsBarVisible(false);
                    completionTimerRef.current = null;
                }, COMPLETION_HIDE_DELAY_MS);
            }

            return;
        }

        clearCompletionTimer();
        completionHoldStartedRef.current = false;
        setIsBarVisible(false);
    }, [isSearching, isAllPolled, searchContext.progressStage]);

    useEffect(() => clearCompletionTimer, []);

    return isBarVisible;
}
